/*
  Shared XP Rank chip + provider hook.
  Reads /api/progression/ladder once and shares via a module-level cache.
  Visually DISTINCT from PackageChip (square-corner plate + shield icon)
  so the three chip families never look alike:
    - PackageChip  (pill,   membership plan)
    - RankChip     (plate,  automatic XP rank)
    - SpecialTitleChip (parallelogram tag, OWNER-assigned title)
*/
import React, { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { api } from "@/lib/api";

const DEFAULTS = [
  { id: "soldier", name: "جندي", min_xp: 0, color: "#94a3b8", badge_url: "" },
];

let _cache = null;
const _subs = new Set();

async function _fetchOnce() {
  if (_cache) return _cache;
  try {
    const { data } = await api.get("/progression/ladder");
    _cache = Array.isArray(data.ranks) && data.ranks.length ? data.ranks : DEFAULTS;
  } catch { _cache = DEFAULTS; }
  _subs.forEach(s => s(_cache));
  return _cache;
}
export function refreshRankLadder() {
  _cache = null;
  return _fetchOnce();
}

export function useRankLadder() {
  const [ladder, setLadder] = useState(_cache || DEFAULTS);
  useEffect(() => {
    let alive = true;
    _fetchOnce().then(v => { if (alive) setLadder(v); });
    const onUpd = (v) => { if (alive) setLadder(v); };
    _subs.add(onUpd);
    return () => { alive = false; _subs.delete(onUpd); };
  }, []);
  return ladder;
}

export function rankFromLadder(xp, ladder) {
  const x = Math.max(0, Number(xp) || 0);
  const list = Array.isArray(ladder) && ladder.length ? ladder : DEFAULTS;
  let cur = list[0];
  for (const r of list) { if (x >= (r.min_xp || 0)) cur = r; }
  return cur;
}

/* Visually distinct plate — sharp corners + shield icon on the left. */
export function RankChip({ xp = 0, rank = null, size = "md" }) {
  const ladder = useRankLadder();
  const r = rank || rankFromLadder(xp, ladder);
  const px = size === "sm" ? 10 : 12;
  return (
    <span data-testid={`rank-chip-${r?.id || "soldier"}`}
          className="inline-flex items-center gap-1.5 font-cairo font-black uppercase tracking-wide"
          style={{
            background: `${r?.color || "#94a3b8"}18`,
            color: r?.color || "#e5e7eb",
            border: `1px solid ${r?.color || "#94a3b8"}55`,
            borderLeftWidth: 4,
            fontSize: `${px}px`,
            padding: size === "sm" ? "1px 8px" : "3px 10px",
            borderRadius: 2,
            letterSpacing: "0.02em",
          }}>
      <Shield size={size === "sm" ? 10 : 12} />
      {r?.name || "جندي"}
    </span>
  );
}

/* Progress bar under the chip — level + xp/next. */
export function LevelProgress({ progress }) {
  if (!progress) return null;
  const { level, xp, xp_current_floor, xp_next_floor, progress_pct } = progress;
  const cur = xp - xp_current_floor;
  const span = xp_next_floor - xp_current_floor;
  return (
    <div className="w-full" data-testid="level-progress">
      <div className="flex items-center justify-between text-[10px] font-cairo text-neutral-300 mb-1">
        <span>المستوى <strong className="text-yellow-300">{level}</strong></span>
        <span className="tabular-nums">
          {cur.toLocaleString()} / {span.toLocaleString()} XP
        </span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-neutral-900 overflow-hidden border border-neutral-800">
        <div className="h-full transition-all"
             style={{ width: `${Math.min(100, progress_pct || 0)}%`,
                      background: "linear-gradient(90deg,#f6c96b 0%,#c026d3 100%)" }} />
      </div>
    </div>
  );
}
